import { useQuery } from '@tanstack/react-query'
import {
  FolderKanban,
  CheckSquare,
  Clock,
  Users,
  AlertCircle,
} from 'lucide-react'
import { TodayPriorities } from './TodayPriorities'
import { MyContents } from './MyContents'
import { TodoWidget } from './TodoWidget'
import { StatCard } from '@/components/common/StatCard'
import { Card, CardHeader } from '@/components/common/Card'
import { Badge } from '@/components/common/Badge'
import { DashboardSkeleton } from './DashboardSkeleton'
import { apiClient } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/lib/utils'

interface ManagerDashboardData {
  kpis?: {
    activeProjects?: number
    teamTasks?: number
    myTasks?: number
    pendingApprovals?: number
    contentThisWeek?: number
    overdueItems?: number
  }
  myTeam?: {
    user: { id: string; firstName?: string; lastName?: string }
    tasksToday?: number
    status?: 'available' | 'busy' | 'away'
  }[]
  approvals?: {
    id: string
    title?: string
    type?: 'content' | 'task'
    client?: string
    submittedAt?: string
  }[]
  myClients?: {
    id: string
    name?: string
    health?: number
    activeProject?: string
    pendingItems?: number
    nextDeadline?: string
  }[]
  recentTasks?: {
    id: string
    title?: string
    assignee?: string
    status?: 'TODO' | 'IN_PROGRESS' | 'IN_REVIEW'
    priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
    dueDate?: string
  }[]
}

const STATUS_LABELS: Record<string, string> = {
  available: 'Disponibile',
  busy: 'Occupato',
  away: 'Assente',
}

const STATUS_COLORS: Record<string, string> = {
  available: 'bg-success-500',
  busy: 'bg-warning-500',
  away: 'bg-slate-300',
}

const TASK_STATUS_LABELS: Record<string, string> = {
  TODO: 'Da Fare',
  IN_PROGRESS: 'In Corso',
  IN_REVIEW: 'In Review',
}

const PRIORITY_COLORS: Record<string, string> = {
  LOW: 'bg-slate-200',
  MEDIUM: 'bg-primary-400',
  HIGH: 'bg-warning-500',
  URGENT: 'bg-danger-500',
}

const EMPTY_DATA: ManagerDashboardData = { kpis: {} }

export function ManagerDashboard() {
  const user = useAuthStore((state) => state.user)

  const { data, isLoading } = useQuery<ManagerDashboardData>({
    queryKey: ['dashboard', 'manager'],
    queryFn: async () => {
      const res = await apiClient.get<any>('/dashboard/manager')
      // Handle nested { type, data: {...} } structure from API
      const outer = res?.data ?? res
      const inner = outer?.data ?? outer
      return (inner ?? {}) as ManagerDashboardData
    },
    refetchInterval: 60_000,
    retry: false,
  })

  if (isLoading && !data) return <DashboardSkeleton />

  const d = data ?? EMPTY_DATA

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Buongiorno, {user?.firstName}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Ecco cosa c&apos;e&apos; da fare oggi
        </p>
      </div>

      {/* Today's Priorities + To-Do */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TodayPriorities />
        <TodoWidget />
      </div>

      {/* My Contents - stato contenuti PED */}
      <MyContents />

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Progetti Attivi"
          value={d.kpis?.activeProjects ?? 0}
          icon={FolderKanban}
          tone="primary"
        />
        <StatCard
          label="Task del Team"
          value={d.kpis?.teamTasks ?? 0}
          icon={CheckSquare}
          tone="success"
        />
        <StatCard
          label="Da Approvare"
          value={d.kpis?.pendingApprovals ?? 0}
          icon={Clock}
          tone="warning"
          hint={(d.kpis?.pendingApprovals ?? 0) > 0 ? 'in attesa' : undefined}
        />
        <StatCard
          label="Contenuti Settimana"
          value={d.kpis?.contentThisWeek ?? 0}
          icon={Users}
          tone="neutral"
        />
      </div>

      {/* Approvals + Team */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pending approvals */}
        <Card padding="md" className="border-slate-200">
          <CardHeader
            title="In Attesa di Approvazione"
            description={`${(d.approvals ?? []).length} elementi`}
            action={
              (d.kpis?.pendingApprovals ?? 0) > 0 ? (
                <Badge variant="warning" dot>
                  <span className="tabular-nums">{d.kpis?.pendingApprovals ?? 0}</span>
                </Badge>
              ) : null
            }
          />
          <div className="space-y-2">
            {(d.approvals ?? []).map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-slate-50 transition-colors cursor-pointer group"
              >
                <div className={cn(
                  'w-1.5 h-8 rounded-full flex-shrink-0',
                  item.type === 'content' ? 'bg-primary-400' : 'bg-warning-400'
                )} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{item.title ?? 'N/A'}</p>
                  <p className="text-xs text-slate-400 tabular-nums">{item.client ?? ''} · {item.submittedAt ?? ''}</p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button className="px-2.5 py-1 text-xs font-medium bg-success-50 text-success-700 rounded-lg hover:bg-success-100 transition-colors">
                    Approva
                  </button>
                  <button className="px-2.5 py-1 text-xs font-medium bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">
                    Vedi
                  </button>
                </div>
              </div>
            ))}
            {(d.approvals ?? []).length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">Nessun elemento in attesa</p>
            )}
          </div>
        </Card>

        {/* Team status */}
        <Card padding="md" className="border-slate-200">
          <CardHeader title="Stato del Team" description="Disponibilita' oggi" />
          <div className="space-y-3">
            {(d.myTeam ?? []).map((member) => (
              <div key={member.user?.id ?? Math.random()} className="flex items-center gap-3">
                <div className="relative flex-shrink-0">
                  <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-slate-600">
                      {(member.user?.firstName ?? '?').charAt(0)}
                    </span>
                  </div>
                  <span
                    className={cn(
                      'absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white',
                      STATUS_COLORS[member.status ?? 'away'] ?? 'bg-slate-300'
                    )}
                  />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">
                    {member.user?.firstName ?? ''} {member.user?.lastName ?? ''}
                  </p>
                  <p className="text-xs text-slate-400">
                    {STATUS_LABELS[member.status ?? 'away'] ?? 'N/A'} · <span className="tabular-nums">{member.tasksToday ?? 0}</span> task oggi
                  </p>
                </div>
                <Badge
                  variant={member.status === 'available' ? 'success' : member.status === 'busy' ? 'warning' : 'neutral'}
                  size="sm"
                >
                  {STATUS_LABELS[member.status ?? 'away'] ?? 'N/A'}
                </Badge>
              </div>
            ))}
            {(d.myTeam ?? []).length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">Nessun membro del team</p>
            )}
          </div>
        </Card>
      </div>

      {/* My clients */}
      <Card padding="md" className="border-slate-200">
        <CardHeader title="I Miei Clienti" description="Panoramica portfolio" />
        <div className="divide-y divide-slate-200">
          {(d.myClients ?? []).map((client) => (
            <div key={client.id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
              <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-semibold text-primary-700">
                  {(client.name ?? '?').charAt(0)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800">{client.name ?? 'N/A'}</p>
                <p className="text-xs text-slate-400 truncate">{client.activeProject ?? ''}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right hidden sm:block">
                  <p className="text-xs text-slate-400">Health</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full',
                          (client.health ?? 0) >= 80 ? 'bg-success-500' :
                          (client.health ?? 0) >= 60 ? 'bg-warning-500' : 'bg-danger-500'
                        )}
                        style={{ width: `${client.health ?? 0}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-slate-600 tabular-nums">{client.health ?? 0}%</span>
                  </div>
                </div>
                {(client.pendingItems ?? 0) > 0 && (
                  <Badge variant="warning" size="sm" dot>
                    <span className="tabular-nums">{client.pendingItems ?? 0}</span> pending
                  </Badge>
                )}
              </div>
            </div>
          ))}
          {(d.myClients ?? []).length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">Nessun cliente</p>
          )}
        </div>
      </Card>

      {/* Recent team tasks */}
      <Card padding="md" className="border-slate-200">
        <CardHeader
          title="Task del Team"
          description="Attivita' in corso"
          action={
            (d.kpis?.overdueItems ?? 0) > 0 ? (
              <div className="flex items-center gap-1 text-xs text-danger-600">
                <AlertCircle className="w-3.5 h-3.5" />
                <span className="tabular-nums">{d.kpis?.overdueItems ?? 0}</span> in ritardo
              </div>
            ) : null
          }
        />
        <div className="space-y-2">
          {(d.recentTasks ?? []).map((task) => (
            <div
              key={task.id}
              className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50 transition-colors"
            >
              <div
                className={cn('w-1 h-6 rounded-full flex-shrink-0', PRIORITY_COLORS[task.priority ?? 'MEDIUM'] ?? 'bg-slate-200')}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{task.title ?? 'N/A'}</p>
                <p className="text-xs text-slate-400">{task.assignee ?? ''}</p>
              </div>
              <Badge
                variant={
                  task.status === 'IN_REVIEW' ? 'warning' :
                  task.status === 'IN_PROGRESS' ? 'info' : 'neutral'
                }
                size="sm"
              >
                {TASK_STATUS_LABELS[task.status ?? 'TODO'] ?? task.status ?? 'N/A'}
              </Badge>
            </div>
          ))}
          {(d.recentTasks ?? []).length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">Nessun task recente</p>
          )}
        </div>
      </Card>
    </div>
  )
}
